/**
 * «Дисциплина» — отчёты смены `K1`–`K6` (§12.2 ТЗ `ops`) без текста бота.
 *
 * Всем: мои чек-листы на сегодня (`K5`) и открытые задачи (`K4`). Тем, у
 * кого «Сводка смены»: сегодняшний день (`K1`), кто не выполнил (`K2`),
 * балл смены за период (`K3`) и по сотрудникам (`K6`). Балл — доля
 * выполненных в своё окно (D77); найденные проблемы его не снижают.
 */

import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import { Link, navigate, useRoute } from '../../router';
import { isoDate } from '../../ui';
import { Tile, RunLine } from './Checklists';
import { LIVE, shiftApi } from './api';
import type { Discipline as Data, DisciplineTotals } from './api';
import { tabPath } from './paths';
import { dayTime } from './shared';

function scoreText(totals: DisciplineTotals | null): string {
  return totals?.score === null || totals?.score === undefined ? '—' : `${totals.score}%`;
}

export function Discipline() {
  const route = useRoute();
  const from = route.params.get('from') ?? undefined;
  const to = route.params.get('to') ?? undefined;
  const query = useQuery<Data, ApiError>({
    queryKey: ['shift', 'discipline', from ?? '', to ?? ''],
    queryFn: () => shiftApi.discipline(from, to),
    ...LIVE,
  });
  const setPeriod = (next: { from?: string; to?: string }) => {
    const params = new URLSearchParams();
    const value = { from, to, ...next };
    if (value.from) params.set('from', value.from);
    if (value.to) params.set('to', value.to);
    const text = params.toString();
    navigate(text ? `/shift/discipline?${text}` : '/shift/discipline', { replace: true });
  };

  if (query.isPending) return <Loading />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const data = query.data;

  return (
    <div className="shift-stack">
      {data.manager && data.day && (
        <section className="shift-card" aria-label="Сегодня">
          <header className="shift-card__head">
            <h2 className="shift-card__title">Сегодня, {isoDate(data.day.date)}</h2>
            <span className="faint">идёт сейчас</span>
          </header>
          <div className="shift-tiles">
            <Tile label="Балл смены" value={scoreText(data.day)} />
            <Tile label="Запланировано" value={`${data.day.planned}`} />
            <Tile label="В окно" value={`${data.day.done_on_time}`} />
            <Tile label="С опозданием" value={`${data.day.done_late}`} />
            <Tile label="Просрочено" value={`${data.day.missed}`} tone={data.day.missed ? 'red' : undefined} />
            <Tile label="Ещё в работе" value={`${data.day.in_work}`} />
          </div>
        </section>
      )}

      {data.manager && data.not_done && (
        <section className="shift-card" aria-label="Кто не выполнил">
          <header className="shift-card__head">
            <h2 className="shift-card__title">Не выполнено сегодня</h2>
            <span className="faint">{data.not_done.length}</span>
          </header>
          {data.not_done.length === 0 ? (
            <p className="faint">Невыполненных чек-листов нет.</p>
          ) : (
            <ul className="shift-list">
              {data.not_done.map((run) => (
                <RunLine key={run.id} run={run} showPosition />
              ))}
            </ul>
          )}
          <p className="faint">
            Перечислены чек-листы, а не люди: у невзятого чек-листа исполнителя нет.
          </p>
        </section>
      )}

      {data.manager && data.score && (
        <section className="shift-card" aria-label="Балл за период">
          <header className="shift-card__head">
            <h2 className="shift-card__title">Балл смены за период</h2>
            <div className="shift-create">
              <label className="shift-field shift-field--short">
                <span>с</span>
                <input
                  className="input"
                  type="date"
                  value={data.period.from}
                  onChange={(event) => setPeriod({ from: event.target.value || undefined })}
                />
              </label>
              <label className="shift-field shift-field--short">
                <span>по</span>
                <input
                  className="input"
                  type="date"
                  value={data.period.to}
                  onChange={(event) => setPeriod({ to: event.target.value || undefined })}
                />
              </label>
            </div>
          </header>
          {data.score.planned === 0 ? (
            <p className="faint">За этот период чек-листов не планировалось — балл считать не из чего.</p>
          ) : (
            <>
              <div className="shift-tiles">
                <Tile
                  label={`Балл (цель ${data.target}%)`}
                  value={scoreText(data.score)}
                  tone={(data.score.score ?? 0) < data.target ? 'red' : undefined}
                />
                <Tile label="Запланировано" value={`${data.score.planned}`} />
                <Tile label="В окно" value={`${data.score.done_on_time}`} />
                <Tile label="С опозданием" value={`${data.score.done_late}`} />
                <Tile label="Просрочено" value={`${data.score.missed}`} />
                <Tile
                  label="Пунктов с проблемой"
                  value={`${data.score.items_problem} из ${data.score.items_total}`}
                />
              </div>
              <p className="faint">
                Доля выполненных в своё окно от запланированных. Найденные проблемы балл не снижают.
              </p>
            </>
          )}
          {data.employees && data.employees.length > 0 && (
            <>
              <h3 className="shift-subtitle">По сотрудникам</h3>
              <ul className="shift-list">
                {data.employees.map((row) => (
                  <li key={row.employee_id} className="shift-row shift-row--static">
                    <span className="shift-row__main">
                      <span className="shift-row__title">{row.name}</span>
                      <span className="faint">
                        заполнил {row.done} из {row.planned} · в окно {row.done_on_time} · с
                        опозданием {row.done_late} · проблем {row.items_problem} из {row.items_total}
                      </span>
                    </span>
                    <span className={`num shift-hours${(row.score ?? 0) < data.target ? ' shift-late' : ''}`}>
                      {scoreText(row)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      <section className="shift-card" aria-label="Мои чек-листы">
        <header className="shift-card__head">
          <h2 className="shift-card__title">Мои чек-листы на сегодня</h2>
          <span className="faint">{data.my_runs.length}</span>
        </header>
        {data.my_runs.length === 0 ? (
          <p className="faint">На сегодня чек-листов для вас нет.</p>
        ) : (
          <ul className="shift-list">
            {data.my_runs.map((run) => (
              <RunLine key={run.id} run={run} showPosition={false} />
            ))}
          </ul>
        )}
      </section>

      <section className="shift-card" aria-label="Открытые задачи">
        <header className="shift-card__head">
          <h2 className="shift-card__title">{data.manager ? 'Открытые задачи' : 'Ваши задачи'}</h2>
          <span className="faint">{data.tasks.length}</span>
        </header>
        {data.tasks.length === 0 ? (
          <p className="faint">{data.manager ? 'Открытых задач нет.' : 'Задач на вас нет.'}</p>
        ) : (
          <ul className="shift-list">
            {data.tasks.map((task) => (
              <li key={task.id}>
                <Link to={tabPath('tasks', task.id)} className="shift-row">
                  <span className="shift-row__main">
                    <span className="shift-row__title">{task.title}</span>
                    <span className="faint">{[task.area, task.assignee].filter(Boolean).join(' · ')}</span>
                  </span>
                  {task.due_at && (
                    <span className={task.overdue ? 'shift-late' : 'faint'}>
                      до {dayTime(task.due_at)}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
